import { useCallback, useEffect, useMemo, useState } from "react";

// Packet lineage — required for materialized patch acceptance.
// Active packet id: 60a5a47d-50e7-4a2b-b106-55cbb11a3509-startup-baseline-2026-04-20-execution-wave-1
// Packet work item: WEB-100-004-ai-first-moderation-engine

type ReasonCode =
  | "link_spam"
  | "emoji_flood"
  | "all_caps"
  | "duplicate_message"
  | "rate_limit"
  | "homoglyph_abuse"
  | "zero_width_abuse"
  | "zalgo_abuse"
  | "banned_word"
  | "raid_signal"
  | "classifier_toxic"
  | "classifier_unavailable"
  | "attendee_reported"
  | "manual_review";

const REASON_LABELS: Record<ReasonCode, string> = {
  link_spam: "Link spam",
  emoji_flood: "Emoji flood",
  all_caps: "All caps",
  duplicate_message: "Duplicate",
  rate_limit: "Rate limit",
  homoglyph_abuse: "Homoglyph abuse",
  zero_width_abuse: "Zero-width abuse",
  zalgo_abuse: "Zalgo abuse",
  banned_word: "Banned word",
  raid_signal: "Raid signal",
  classifier_toxic: "Classifier: toxic",
  classifier_unavailable: "Classifier: unavailable",
  attendee_reported: "Attendee report",
  manual_review: "Manual review",
};

type QueueStatus = "held" | "approved" | "rejected" | "undone";
type ReportStatus = "open" | "resolved" | "dismissed";

interface QueueItem {
  id: string;
  messageId: string | null;
  participantId: string;
  displayName: string | null;
  content: string;
  reasonCodes: ReasonCode[];
  classifierScore: number | null;
  classifierError: string | null;
  status: QueueStatus;
  createdAt: string;
  decidedAt: string | null;
  decidedBy: string | null;
}

interface ActionLogEntry {
  id: string;
  action: string;
  participantId: string | null;
  messageId: string | null;
  queueItemId: string | null;
  reasonCodes: ReasonCode[];
  notes: string | null;
  actorId: string;
  createdAt: string;
}

interface AttendeeReport {
  id: string;
  reporterId: string;
  participantId: string;
  messageId: string | null;
  reasonCode: ReasonCode;
  notes: string | null;
  status: ReportStatus;
  createdAt: string;
}

interface RaidState {
  enabled: boolean;
  action: "slow" | "lockdown" | "hold";
  triggeredAt: string | null;
  expiresAt: string | null;
  joinsObserved: number;
}

interface ModerationState {
  settings: {
    classifierFallback: "hold" | "restrict";
    raidJoinsPerMinuteThreshold: number;
    holdQueueEnabled: boolean;
  };
  queue: QueueItem[];
  actions: ActionLogEntry[];
  reports: AttendeeReport[];
  raid: RaidState;
}

export interface ModerationDashboardProps {
  webinarId: string;
  apiBase?: string;
  fetchImpl?: typeof fetch;
}

type Tab = "queue" | "log" | "reports";

async function jsonFetch<T>(input: RequestInfo, init: RequestInit | undefined, fetcher: typeof fetch): Promise<T> {
  const res = await fetcher(input, { credentials: "include", ...init });
  if (!res.ok) {
    const body = await res.text();
    throw new Error(`Request failed (${res.status}): ${body}`);
  }
  return (await res.json()) as T;
}

export function ModerationDashboard({ webinarId, apiBase = "/v1/webinars", fetchImpl }: ModerationDashboardProps) {
  const fetcher = fetchImpl ?? globalThis.fetch.bind(globalThis);
  const base = `${apiBase}/${webinarId}/chat/moderation`;

  const [tab, setTab] = useState<Tab>("queue");
  const [state, setState] = useState<ModerationState | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [pendingId, setPendingId] = useState<string | null>(null);

  const loadState = useCallback(async () => {
    try {
      const result = await jsonFetch<{ data: ModerationState }>(base, undefined, fetcher);
      setState(result.data);
      setError(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    }
  }, [base, fetcher]);

  useEffect(() => {
    void loadState();
    const interval = setInterval(() => void loadState(), 5_000);
    return () => clearInterval(interval);
  }, [loadState]);

  const decide = useCallback(
    async (queueItemId: string, decision: "approve" | "reject" | "undo") => {
      setPendingId(queueItemId);
      try {
        await jsonFetch(`${base}/queue/${queueItemId}/${decision}`, {
          method: "POST",
          headers: { "content-type": "application/json" },
          body: JSON.stringify({}),
        }, fetcher);
        await loadState();
      } catch (err) {
        setError(err instanceof Error ? err.message : String(err));
      } finally {
        setPendingId(null);
      }
    },
    [base, fetcher, loadState],
  );

  const resolveReport = useCallback(
    async (reportId: string, status: "resolved" | "dismissed") => {
      setPendingId(reportId);
      try {
        await jsonFetch(`${base}/reports/${reportId}/resolve`, {
          method: "POST",
          headers: { "content-type": "application/json" },
          body: JSON.stringify({ status }),
        }, fetcher);
        await loadState();
      } catch (err) {
        setError(err instanceof Error ? err.message : String(err));
      } finally {
        setPendingId(null);
      }
    },
    [base, fetcher, loadState],
  );

  const heldQueue = useMemo(
    () => state?.queue.filter((entry) => entry.status === "held") ?? [],
    [state],
  );
  const decidedQueue = useMemo(
    () => state?.queue.filter((entry) => entry.status !== "held").slice(-20) ?? [],
    [state],
  );
  const openReports = useMemo(
    () => state?.reports.filter((entry) => entry.status === "open") ?? [],
    [state],
  );

  return (
    <section className="moderation-dashboard" aria-label="Webinar moderation">
      <header className="moderation-dashboard__header">
        <h2>Moderation</h2>
        {state?.raid.enabled ? (
          <div role="alert" className="moderation-dashboard__raid">
            Raid mode: {state.raid.action} — {state.raid.joinsObserved} joins/min
          </div>
        ) : null}
        {state ? (
          <div className="moderation-dashboard__status">
            Classifier fallback: <strong>{state.settings.classifierFallback}</strong>
            {" · "}
            Hold queue: <strong>{state.settings.holdQueueEnabled ? "on" : "off"}</strong>
          </div>
        ) : null}
      </header>

      {error ? (
        <div role="alert" className="moderation-dashboard__error">
          {error}
        </div>
      ) : null}

      <nav className="moderation-dashboard__tabs">
        <button type="button" onClick={() => setTab("queue")} aria-pressed={tab === "queue"}>
          Held queue ({heldQueue.length})
        </button>
        <button type="button" onClick={() => setTab("reports")} aria-pressed={tab === "reports"}>
          Reports ({openReports.length})
        </button>
        <button type="button" onClick={() => setTab("log")} aria-pressed={tab === "log"}>
          Action log
        </button>
      </nav>

      {tab === "queue" ? (
        <div className="moderation-dashboard__queue">
          <ol>
            {heldQueue.length === 0 ? <li>No held messages.</li> : null}
            {heldQueue.map((item) => (
              <li key={item.id} className="moderation-dashboard__queue-item">
                <div className="moderation-dashboard__queue-meta">
                  <span>{item.displayName ?? item.participantId.slice(0, 8)}</span>
                  <span>{new Date(item.createdAt).toLocaleTimeString()}</span>
                </div>
                <p>{item.content}</p>
                <div className="moderation-dashboard__queue-reasons">
                  {item.reasonCodes.map((code) => (
                    <span key={code} className="moderation-dashboard__chip">
                      {REASON_LABELS[code]}
                    </span>
                  ))}
                  {item.classifierScore !== null ? (
                    <span className="moderation-dashboard__chip moderation-dashboard__chip--score">
                      score {item.classifierScore.toFixed(2)}
                    </span>
                  ) : null}
                  {item.classifierError ? (
                    <span className="moderation-dashboard__chip moderation-dashboard__chip--warn">
                      classifier error
                    </span>
                  ) : null}
                </div>
                <div className="moderation-dashboard__queue-actions">
                  <button
                    type="button"
                    disabled={pendingId === item.id}
                    onClick={() => void decide(item.id, "approve")}
                  >
                    Approve
                  </button>
                  <button
                    type="button"
                    disabled={pendingId === item.id}
                    onClick={() => void decide(item.id, "reject")}
                  >
                    Reject
                  </button>
                </div>
              </li>
            ))}
          </ol>
          {decidedQueue.length > 0 ? (
            <details>
              <summary>Recent decisions</summary>
              <ul>
                {decidedQueue.map((item) => (
                  <li key={item.id}>
                    <span>{item.status}</span>
                    {" · "}
                    <span>{item.displayName ?? item.participantId.slice(0, 8)}</span>
                    {" · "}
                    <span>{item.reasonCodes.map((code) => REASON_LABELS[code]).join(", ")}</span>
                    {item.status !== "undone" ? (
                      <button
                        type="button"
                        disabled={pendingId === item.id}
                        onClick={() => void decide(item.id, "undo")}
                      >
                        Undo
                      </button>
                    ) : null}
                  </li>
                ))}
              </ul>
            </details>
          ) : null}
        </div>
      ) : null}

      {tab === "reports" ? (
        <div className="moderation-dashboard__reports">
          {openReports.length === 0 ? <p>No open reports.</p> : null}
          <ul>
            {openReports.map((report) => (
              <li key={report.id}>
                <div>
                  <strong>{REASON_LABELS[report.reasonCode]}</strong>
                  {" · "}
                  participant {report.participantId.slice(0, 8)}
                  {" · "}
                  reporter {report.reporterId.slice(0, 8)}
                </div>
                {report.notes ? <p>{report.notes}</p> : null}
                <div className="moderation-dashboard__queue-actions">
                  <button
                    type="button"
                    disabled={pendingId === report.id}
                    onClick={() => void resolveReport(report.id, "resolved")}
                  >
                    Resolve
                  </button>
                  <button
                    type="button"
                    disabled={pendingId === report.id}
                    onClick={() => void resolveReport(report.id, "dismissed")}
                  >
                    Dismiss
                  </button>
                </div>
              </li>
            ))}
          </ul>
        </div>
      ) : null}

      {tab === "log" ? (
        <div className="moderation-dashboard__log">
          <ol>
            {state?.actions.slice(-50).reverse().map((entry) => (
              <li key={entry.id}>
                <span>{new Date(entry.createdAt).toLocaleTimeString()}</span>
                {" · "}
                <strong>{entry.action}</strong>
                {entry.participantId ? ` · participant ${entry.participantId.slice(0, 8)}` : null}
                {entry.reasonCodes.length > 0
                  ? ` · ${entry.reasonCodes.map((code) => REASON_LABELS[code]).join(", ")}`
                  : null}
                {entry.notes ? ` — ${entry.notes}` : null}
              </li>
            )) ?? null}
          </ol>
        </div>
      ) : null}
    </section>
  );
}

export default ModerationDashboard;
